import { useCallback, useEffect, useMemo, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import {
  formatDateRu,
  formatTimeRu,
  toDateInputValue,
} from "../../../utils/appointmentLabels";
import {
  attendanceStatusLabel,
  attendanceStatusTone,
} from "../../../utils/attendanceDisplay";
import AcceptMembershipPayment from "../common/AcceptMembershipPayment";
import CancelPendingMembershipButton from "../common/CancelPendingMembershipButton";
import {
  formatDateShort,
  membershipStatusLabel,
  membershipStatusTone,
  paymentMethodLabel,
} from "./clientSportDisplay";
import {
  analyzeMembershipAssignBlock,
  isMembershipCurrentlyActive,
} from "../../../utils/membershipAssignUtils";
import s from "../clinic.module.css";
import { Button, Field, Input, Select, useToast } from "../../ui";

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

function fmtMoney(n) {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));
}

function monthRange(year, month) {
  const from = new Date(year, month, 1);
  const to = new Date(year, month + 1, 0);
  return {
    from: toDateInputValue(from),
    to: toDateInputValue(to),
  };
}

function buildMonthCells(year, month) {
  const first = new Date(year, month, 1);
  const startPad = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    const m = String(month + 1).padStart(2, "0");
    const day = String(d).padStart(2, "0");
    cells.push(`${year}-${m}-${day}`);
  }
  return cells;
}

function dateKey(iso) {
  if (!iso) return "";
  return String(iso).slice(0, 10);
}

function dayTonePriority(tone) {
  const order = { present: 4, excused: 3, absent: 2, expected: 1, scheduled: 0.5 };
  return order[tone] || 0;
}

function miniCalClass(tone) {
  if (tone === "present") return s.attendanceMiniPresent;
  if (tone === "absent") return s.attendanceMiniAbsent;
  if (tone === "excused") return s.attendanceMiniExcused;
  if (tone === "expected") return s.attendanceMiniExpected;
  if (tone === "scheduled") return s.attendanceMiniScheduled;
  return "";
}

function membershipTone(status, notes) {
  const tone = membershipStatusTone(status, notes);
  if (tone === "ok" || tone === "success") return "active";
  if (tone === "warn") return status === "pending" ? "pending" : "warn";
  if (status === "expired") return "expired";
  return "muted";
}

export function PatientMembershipPanel({ patientId, payments, onChanged, compact = false }) {
  const toast = useToast();
  const [memberships, setMemberships] = useState([]);
  const [plans, setPlans] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [renewalMode, setRenewalMode] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const [assignForm, setAssignForm] = useState({
    plan_id: "",
    starts_at: new Date().toISOString().slice(0, 10),
  });
  const [assignBusy, setAssignBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, p] = await Promise.all([
        clinicAPI.getClientMemberships({ patient_id: patientId }),
        clinicAPI.getMembershipPlans(),
      ]);
      setMemberships(m.memberships || []);
      setPlans(p.plans || []);
    } catch {
      setMemberships([]);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const active = memberships.find(isMembershipCurrentlyActive);
  const pending = memberships.find((m) => m.status === "pending");
  const assignBlock = analyzeMembershipAssignBlock(memberships, patientId);

  const openAssign = () => {
    setRenewalMode(false);
    setShowAssign(true);
  };

  const openRenewal = () => {
    if (!assignBlock.renewalStartsAt) return;
    setAssignForm((f) => ({
      ...f,
      starts_at: assignBlock.renewalStartsAt,
    }));
    setRenewalMode(true);
    setShowAssign(true);
  };

  const assign = async (e) => {
    e.preventDefault();
    if (!assignForm.plan_id) {
      toast.error("Выберите тариф");
      return;
    }
    if (!renewalMode && assignBlock.blocked) {
      toast.error(assignBlock.message || "Нельзя выдать абонемент");
      return;
    }
    setAssignBusy(true);
    try {
      const data = await clinicAPI.createClientMembership({
        patient_id: patientId,
        renewal: renewalMode,
        ...assignForm,
      });
      const activated = data.membership?.status === "active";
      toast.success(
        activated
          ? "Абонемент выдан — найдена предоплата, тариф активирован"
          : renewalMode
            ? "Продление оформлено — примите оплату в Финансы → Должники"
            : "Абонемент выдан — примите оплату в Финансы → Должники",
      );
      setAssignForm((f) => ({ ...f, plan_id: "" }));
      setRenewalMode(false);
      setShowAssign(false);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setAssignBusy(false);
    }
  };

  return (
    <div className={s.clientProfileTabPanel}>
      {pending ? (
        <section className={s.clientTabCard}>
          <h3 className={s.clientTabCardTitle}>Ожидает оплаты</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <AcceptMembershipPayment
              membership={pending}
              patientId={patientId}
              onSuccess={() => {
                load();
                onChanged?.();
              }}
            />
            <CancelPendingMembershipButton
              membership={pending}
              onSuccess={() => {
                load();
                onChanged?.();
              }}
            />
          </div>
        </section>
      ) : null}

      {active ? (
        <section className={s.clientTabCard}>
          <h3 className={s.clientTabCardTitle}>Активный абонемент</h3>
          <p className={s.clientTabCardLead}>
            <strong>{active.plan?.name}</strong>
            {active.visits_remaining != null
              ? ` · осталось ${active.visits_remaining} зан.`
              : ""}
            {active.ends_at
              ? ` · до ${formatDateShort(active.ends_at)}`
              : ""}
          </p>
        </section>
      ) : (
        <section className={s.clientTabCard}>
          <p className={s.clientTabMuted}>Нет активного абонемента</p>
        </section>
      )}

      {compact && !showAssign && !pending ? (
        <div className={s.clientTabFormRow} style={{ flexWrap: "wrap", gap: 8 }}>
          {!assignBlock.blocked ? (
            <button
              type="button"
              className={s.clientProfileInlineLink}
              onClick={openAssign}
            >
              + Выдать абонемент
            </button>
          ) : null}
          {assignBlock.canRenew ? (
            <button
              type="button"
              className={s.clientProfileInlineLink}
              onClick={openRenewal}
            >
              Продлить с {formatDateShort(assignBlock.renewalStartsAt)}
            </button>
          ) : null}
          {assignBlock.blocked && !assignBlock.canRenew ? (
            <p className={s.clientTabMuted}>{assignBlock.message}</p>
          ) : null}
        </div>
      ) : null}

      {(!compact || showAssign) && !pending ? (
        <section className={s.clientTabCard}>
          {compact ? (
            <div className={s.clientProfileEmbeddedHead}>
              <h3 className={s.clientTabCardTitle}>
                {renewalMode ? "Продление абонемента" : "Выдать абонемент"}
              </h3>
              <button
                type="button"
                className={s.clientProfileSectionAction}
                onClick={() => {
                  setShowAssign(false);
                  setRenewalMode(false);
                }}
              >
                Скрыть
              </button>
            </div>
          ) : (
            <h3 className={s.clientTabCardTitle}>
              {renewalMode ? "Продление абонемента" : "Выдать абонемент"}
            </h3>
          )}
          {renewalMode ? (
            <p className={s.clientTabMuted}>
              Новый абонемент начнётся после окончания текущего. Статус — ожидает
              оплаты.
            </p>
          ) : assignBlock.blocked ? (
            <p className={s.clientTabMuted}>{assignBlock.message}</p>
          ) : null}
          {(renewalMode || !assignBlock.blocked) ? (
          <form onSubmit={assign} className={s.clientTabFormRow}>
            <Field label="Тариф" className={s.clientTabFieldGrow}>
              <Select
                value={assignForm.plan_id}
                onChange={(e) =>
                  setAssignForm({ ...assignForm, plan_id: e.target.value })
                }
              >
                <option value="">Выберите…</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.price != null ? ` · ${fmtMoney(p.price)}` : ""}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="С даты">
              <Input
                type="date"
                value={assignForm.starts_at}
                min={renewalMode ? assignBlock.renewalStartsAt || undefined : undefined}
                onChange={(e) =>
                  setAssignForm({ ...assignForm, starts_at: e.target.value })
                }
              />
            </Field>
            <div className={s.clientTabFormBtn}>
              <Button type="submit" disabled={assignBusy}>
                {renewalMode ? "Оформить продление" : "Выдать"}
              </Button>
            </div>
          </form>
          ) : assignBlock.canRenew && !renewalMode ? (
            <Button type="button" variant="secondary" onClick={openRenewal}>
              Продлить абонемент
            </Button>
          ) : null}
        </section>
      ) : null}

      {memberships.length > 0 && (!compact || showHistory) ? (
        <section className={s.clientTabCard}>
          <h3 className={s.clientTabCardTitle}>Все абонементы</h3>
          <div className={s.membershipHistoryWrap}>
            <table className={s.membershipHistoryTable}>
              <thead>
                <tr>
                  <th>Тариф</th>
                  <th>Период</th>
                  <th>Остаток</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                {memberships.map((m) => (
                  <tr
                    key={m.id}
                    className={s[`clientDataTableRow_${membershipTone(m.status, m.notes)}`]}
                  >
                    <td>{m.plan?.name || "—"}</td>
                    <td>
                      {m.starts_at ? formatDateShort(m.starts_at) : "—"}
                      {m.ends_at ? ` — ${formatDateShort(m.ends_at)}` : ""}
                    </td>
                    <td>
                      {m.visits_remaining != null
                        ? `${m.visits_remaining} зан.`
                        : "—"}
                    </td>
                    <td>{membershipStatusLabel(m.status, m.notes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {compact && !showHistory && (memberships.length > 0 || payments?.length > 0) ? (
        <button
          type="button"
          className={s.clientProfileInlineLink}
          onClick={() => setShowHistory(true)}
        >
          Показать историю абонементов и платежей
        </button>
      ) : null}

      {payments?.length > 0 && (!compact || showHistory) ? (
        <section className={s.clientTabCard}>
          <h3 className={s.clientTabCardTitle}>Платежи</h3>
          <ul className={s.clientSimpleList}>
            {payments.map((p) => (
              <li key={p.id}>
                <strong>{fmtMoney(p.amount)}</strong>
                <span className={s.clientTabMuted}>
                  {" "}
                  · {formatDateShort(p.paid_at)} · {paymentMethodLabel(p.method)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

export function PatientAttendancePanel({ patientId }) {
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { from, to } = monthRange(viewYear, viewMonth);
    try {
      const r = await clinicAPI.getPatientSportAttendance(patientId, { from, to });
      setData(r);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [patientId, viewYear, viewMonth]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = data?.attendances || [];
  const stats = data?.stats;

  const dayMap = useMemo(() => {
    const map = {};
    for (const a of rows) {
      const key = dateKey(a.session?.start_datetime);
      if (!key) continue;
      const tone = attendanceStatusTone(a);
      if (!map[key] || dayTonePriority(tone) > dayTonePriority(map[key])) {
        map[key] = tone;
      }
    }
    return map;
  }, [rows]);

  const cells = useMemo(
    () => buildMonthCells(viewYear, viewMonth),
    [viewYear, viewMonth],
  );

  const monthTitle = new Date(viewYear, viewMonth, 1).toLocaleDateString(
    "ru-RU",
    { month: "long", year: "numeric" },
  );

  const goMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
    setSelectedDay(null);
  };

  const sorted = useMemo(() => {
    let list = [...rows];
    if (selectedDay) {
      list = list.filter((a) => dateKey(a.session?.start_datetime) === selectedDay);
    }
    return list.sort((a, b) => {
      const ta = new Date(a.session?.start_datetime || 0).getTime();
      const tb = new Date(b.session?.start_datetime || 0).getTime();
      return tb - ta;
    });
  }, [rows, selectedDay]);

  const monthPct =
    stats && stats.present + stats.absent > 0
      ? Math.round((stats.present / (stats.present + stats.absent)) * 100)
      : null;

  const logToneClass = (tone) => {
    if (tone === "present") return s.clientAttendanceLogPresent;
    if (tone === "absent") return s.clientAttendanceLogAbsent;
    if (tone === "excused") return s.clientAttendanceLogExcused;
    if (tone === "scheduled") return s.clientAttendanceLogScheduled;
    return s.clientAttendanceLogExpected;
  };

  const membershipHint =
    data?.membership?.plan_type === "visits" &&
    data.membership.visits_remaining != null
      ? `${data.membership.visits_remaining} зан. по абонементу`
      : data?.membership?.ends_at
        ? `до ${formatDateShort(data.membership.ends_at)}`
        : null;

  return (
    <div className={s.clientProfileTabPanel}>
      {stats ? (
        <div className={s.clientAttendanceSummary}>
          <div className={s.clientAttendanceSummaryItem}>
            <span className={s.clientAttendanceSummaryVal}>{stats.present}</span>
            <span className={s.clientAttendanceSummaryLbl}>был</span>
          </div>
          <div className={s.clientAttendanceSummaryItem}>
            <span className={s.clientAttendanceSummaryVal}>{stats.absent}</span>
            <span className={s.clientAttendanceSummaryLbl}>пропуск</span>
          </div>
          {(data?.membership || (stats.expected ?? 0) > 0) ? (
            <div className={s.clientAttendanceSummaryItem}>
              <span className={s.clientAttendanceSummaryVal}>
                {stats.expected ?? 0}
              </span>
              <span className={s.clientAttendanceSummaryLbl}>
                ожидается
                {membershipHint ? (
                  <small className={s.clientAttendanceSummarySub}>
                    {membershipHint}
                  </small>
                ) : null}
              </span>
            </div>
          ) : (
            <div className={s.clientAttendanceSummaryItem}>
              <span className={s.clientAttendanceSummaryVal}>
                {monthPct != null ? `${monthPct}%` : "—"}
              </span>
              <span className={s.clientAttendanceSummaryLbl}>за месяц</span>
            </div>
          )}
        </div>
      ) : null}

      <section className={s.clientTabCard}>
        <div className={s.clientAttendanceCalHead}>
          <button
            type="button"
            className={s.clientAttendanceCalNav}
            onClick={() => goMonth(-1)}
            aria-label="Предыдущий месяц"
          >
            ‹
          </button>
          <h3 className={s.clientTabCardTitle}>{monthTitle}</h3>
          <button
            type="button"
            className={s.clientAttendanceCalNav}
            onClick={() => goMonth(1)}
            aria-label="Следующий месяц"
          >
            ›
          </button>
        </div>

        <div className={s.attendanceMiniWeekdays}>
          {WEEKDAYS.map((w) => (
            <span key={w} className={s.attendanceMiniWeekday}>
              {w}
            </span>
          ))}
        </div>

        <div className={s.attendanceMiniCalWide}>
          {cells.map((dateStr, idx) => {
            if (!dateStr) {
              return (
                <span
                  key={`empty-${idx}`}
                  className={`${s.attendanceMiniDay} ${s.attendanceMiniDay_empty}`}
                  aria-hidden
                />
              );
            }
            const tone = dayMap[dateStr];
            const isSelected = selectedDay === dateStr;
            const dayNum = Number(dateStr.slice(8, 10));
            return (
              <button
                key={dateStr}
                type="button"
                className={`${s.attendanceMiniDay} ${miniCalClass(tone)} ${
                  isSelected ? s.attendanceMiniDay_selected : ""
                }`}
                onClick={() =>
                  setSelectedDay((d) => (d === dateStr ? null : dateStr))
                }
                title={
                  tone
                    ? {
                        present: "Был",
                        absent: "Не был",
                        excused: "Предупредил",
                        expected: "Ожидается по абонементу",
                        scheduled: "Тренировка вне лимита абонемента",
                      }[tone]
                    : "Нет занятия"
                }
              >
                {dayNum}
              </button>
            );
          })}
        </div>

        <div className={s.attendanceMiniLegend}>
          <span>
            <i className={s.attendanceMiniPresent} /> был
          </span>
          <span>
            <i className={s.attendanceMiniAbsent} /> не был
          </span>
          <span>
            <i className={s.attendanceMiniExcused} /> предупредил
          </span>
          <span>
            <i className={s.attendanceMiniExpected} /> ожидается
          </span>
          <span>
            <i className={s.attendanceMiniScheduled} /> вне абонемента
          </span>
        </div>
        <p className={s.clientAttendanceHint}>
          Нажмите на день, чтобы отфильтровать список. «Ожидается» — ближайшие
          занятия в пределах абонемента (не больше остатка занятий). Серые дни —
          тренировки сверх лимита или без абонемента.
        </p>
      </section>

      <section className={s.clientTabCard}>
        <div className={s.clientAttendanceLogHead}>
          <h3 className={s.clientTabCardTitle}>
            {selectedDay
              ? `Занятия ${formatDateRu(`${selectedDay}T12:00:00`)}`
              : "Все занятия за месяц"}
          </h3>
          {selectedDay ? (
            <button
              type="button"
              className={s.clientAttendanceClearFilter}
              onClick={() => setSelectedDay(null)}
            >
              Показать все
            </button>
          ) : null}
        </div>

        {loading ? (
          <p className={s.clientTabMuted}>Загрузка…</p>
        ) : sorted.length === 0 ? (
          <p className={s.clientTabMuted}>Нет занятий за выбранный период</p>
        ) : (
          <ul className={s.clientAttendanceLog}>
            {sorted.map((a) => {
              const tone = attendanceStatusTone(a);
              return (
                <li key={a.id} className={s.clientAttendanceLogItem}>
                  <div>
                    <strong>
                      {formatDateRu(a.session?.start_datetime)}{" "}
                      {formatTimeRu(a.session?.start_datetime)}
                    </strong>
                    <span className={s.clientTabMuted}>
                      {" "}
                      · {a.session?.group?.name || "—"}
                    </span>
                  </div>
                  <span className={logToneClass(tone)}>
                    {attendanceStatusLabel(a)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
